import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Package, Code, Factory, Truck, Tag, Shield } from "lucide-react";
import { ProductReference } from "@/data/productReference";

interface ProductMetadataProps {
  product: ProductReference;
  showInternalCodes?: boolean; // Toggle for customer vs internal view
  className?: string;
}

export const ProductMetadata = ({ 
  product, 
  showInternalCodes = false,
  className = "" 
}: ProductMetadataProps) => {
  return (
    <Card className={`bg-muted/20 border-muted ${className}`}>
      <CardContent className="p-4">
        {/* Customer-Facing Information */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{product.avtName}</h3>
              <p className="text-sm text-muted-foreground">{product.avtSeries}</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              {product.category.replace('-', ' ').toUpperCase()}
            </Badge>
          </div>

          <Separator />

          {/* Quick Specs Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <Shield className="w-3 h-3 text-primary" />
                <span className="text-muted-foreground">Warranty:</span>
                <span className="font-medium">{product.specifications.warranty}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Package className="w-3 h-3 text-primary" />
                <span className="text-muted-foreground">Installation:</span>
                <span className="font-medium">{product.specifications.installation}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <Tag className="w-3 h-3 text-primary" />
                <span className="text-muted-foreground">Category:</span>
                <span className="font-medium capitalize">{product.priceRange}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Dimensions: {product.specifications.dimensions}
              </div>
            </div>
          </div>

          {/* Internal Codes Section - Hidden by default */}
          {showInternalCodes && (
            <>
              <Separator />
              <div className="bg-muted/30 p-3 rounded-lg space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <Code className="w-3 h-3" />
                  Internal Reference Codes
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Internal SKU:</span>
                    <code className="bg-background px-1 py-0.5 rounded text-xs font-mono">
                      {product.internalCode}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Factory Model:</span>
                    <code className="bg-background px-1 py-0.5 rounded text-xs font-mono">
                      {product.factoryModel}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Logistics:
                    </span>
                    <code className="bg-background px-1 py-0.5 rounded text-xs font-mono">
                      {product.logisticsCode}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Factory className="w-3 h-3" />
                      URL Slug:
                    </span>
                    <code className="bg-background px-1 py-0.5 rounded text-xs font-mono">
                      {product.urlSlug}
                    </code>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Alternative compact version for product listings
export const ProductMetadataCompact = ({ 
  product, 
  showInternalCodes = false 
}: ProductMetadataProps) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 bg-muted/10 rounded-lg border">
      <div className="flex items-center gap-3">
        <Badge variant="outline" className="text-xs">
          {product.avtName}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {product.tagline}
        </span>
      </div>
      
      {showInternalCodes && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>SKU:</span>
          <code className="bg-background px-1 py-0.5 rounded font-mono">
            {product.internalCode}
          </code>
          <span>|</span>
          <span>Logistics:</span>
          <code className="bg-background px-1 py-0.5 rounded font-mono">
            {product.logisticsCode}
          </code>
        </div>
      )}
    </div>
  );
};